import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioButton,
} from '../../components/BrioComponents';
import { measureValue } from '../hooks/useBoqMeasurements';
import { useRangeSelection } from '../hooks/useRangeSelection';
import { formatQty } from '../utils/money';

const fmt = (v) => (v === null || v === undefined ? '—' : formatQty(v));

// Pick existing project measurements (not already on this line) and link them.
// This is the "measure once, bill many" action: the same wall measured for
// brick can be linked to plaster and paint lines too.
function LinkMeasurementsModal({ line, basis, measurements, codeForItem, onClose, onLink }) {
  const [asDeduction, setAsDeduction] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const candidates = useMemo(
    () => measurements.filter((m) => !m.itemIds.includes(line.id)),
    [measurements, line.id]
  );

  const { selected, isSelected, toggle } = useRangeSelection(candidates.map((m) => m.id));

  const handleLink = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    setError(null);
    const result = await onLink([...selected], asDeduction);
    setSubmitting(false);
    if (result.success) onClose();
    else setError(result.error || 'Failed to link measurements.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleLink} disabled={selected.size === 0 || submitting}>
        {submitting ? 'Linking…' : `Link ${selected.size || ''}`.trim()}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title={`Link existing measurements to ${line.version?.fullCode || ''}`} onClose={onClose} width="xl" footer={footer}>
      <BrioModalBody>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>{error}</div>
        )}
        <p style={{ marginTop: 0, fontSize: '0.88rem', color: 'var(--brio-text-muted)' }}>
          Pick measurements already taken off elsewhere in this project. They'll feed this line on its
          basis (<strong>{basis || 'manual'}</strong>) without being re-measured.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', marginBottom: '0.75rem' }}>
          <input type="checkbox" checked={asDeduction} onChange={(e) => setAsDeduction(e.target.checked)} />
          Link as <strong>deduction</strong> (subtract from this line — e.g. window/door openings, shafts)
        </label>
        {candidates.length === 0 ? (
          <div style={{ padding: '1rem', color: 'var(--brio-text-muted)' }}>
            No other measurements available to link.
          </div>
        ) : (
          <div style={{ maxHeight: 420, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.5rem', width: 34 }} />
                  <th style={{ padding: '0.4rem 0.5rem' }}>Description</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Currently feeds</th>
                  <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>This basis</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((m) => (
                  <tr key={m.id} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)', cursor: 'pointer', userSelect: 'none' }} onClick={(e) => toggle(m.id, e)}>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      <input type="checkbox" checked={isSelected(m.id)} onChange={() => {}} onClick={(e) => { e.stopPropagation(); toggle(m.id, e); }} />
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem' }}>
                      {m.description || <span style={{ color: 'var(--brio-text-muted)' }}>(no description)</span>}
                      <span style={{ color: 'var(--brio-text-muted)', marginLeft: 8, fontSize: '0.78rem' }}>
                        {[m.nr, m.length, m.breadth, m.height].filter((x) => x != null).join(' × ')}
                      </span>
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
                      {m.itemIds.map(codeForItem).join(', ') || '—'}
                    </td>
                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                      {basis ? fmt(measureValue(m, basis)) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

export default LinkMeasurementsModal;
